import React, { useEffect, useState } from 'react';
import { Menu, X, Phone, PhoneCall, Globe } from 'lucide-react';
import { useLanguage } from './LanguageContext';
export function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const {
    language,
    setLanguage,
    t
  } = useLanguage();
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const navLinks = [{
    name: t.nav.home,
    href: '#home'
  }, {
    name: t.nav.services,
    href: '#services'
  }, {
    name: t.nav.about,
    href: '#about'
  }, {
    name: t.nav.process,
    href: '#process'
  }, {
    name: t.nav.testimonials,
    href: '#testimonials'
  }, {
    name: t.nav.contact,
    href: '#contact'
  }];
  return <>
      {/* Top Bar */}
      <div className="bg-brand-dark text-white text-sm hidden md:block" data-id="element-160">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center py-2" data-id="element-161">
          <div className="flex items-center gap-6" data-id="element-162">
            <a href="tel:3054170530" className="flex items-center gap-2 hover:text-brand-gold transition-colors" data-id="element-163">
              <Phone size={14} data-id="element-164" />
              <span data-id="element-165">(305) 417-0530</span>
            </a>
            <a href="tel:3057857829" className="flex items-center gap-2 hover:text-brand-gold transition-colors" data-id="element-166">
              <Phone size={14} data-id="element-167" />
              <span data-id="element-168">(305) 785-7829</span>
            </a>
            <span className="text-gray-400" data-id="element-169">|</span>
            <a href="mailto:elitepropertyadjusters@gmail.com" className="hover:text-brand-gold transition-colors" data-id="element-170">
              elitepropertyadjusters@gmail.com
            </a>
          </div>
          <div className="text-brand-gold font-semibold tracking-wide text-xs uppercase" data-id="element-171">
            {t.nav.topbar}
          </div>
        </div>
      </div>

      {/* Main Navbar */}
      <nav className={`fixed w-full z-50 transition-all duration-500 ${isScrolled ? 'bg-brand-dark/95 backdrop-blur-md shadow-lg py-2 top-0' : 'bg-brand-dark/80 backdrop-blur-sm py-4 md:top-[40px] top-0'}`} data-id="element-172">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-173">
          <div className="flex justify-between items-center" data-id="element-174">
            {/* Logo */}
            <a href="#home" className="flex-shrink-0 group" data-id="element-175">
              <img src="https://cdn.magicpatterns.com/uploads/ocQwAnLv8ZRS6ZM4MrGo2A/LOGO_-_ELITE_-_DORADO.png" alt="Elite Property Adjusters LLC Logo" className={`w-auto transition-all duration-300 ${isScrolled ? 'h-12' : 'h-16'}`} data-id="element-176" />
            </a>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center gap-1" data-id="element-177">
              {navLinks.map(link => <a key={link.name} href={link.href} className={`relative px-4 py-2 text-sm font-bold uppercase tracking-wider transition-colors rounded-sm
                    ${isScrolled ? 'text-gray-200 hover:text-white hover:bg-white/10' : 'text-gray-200 hover:text-white hover:bg-white/10'}
                  `} data-id="element-178">
                  {link.name}
                </a>)}
            </div>

            {/* Right Side Actions */}
            <div className="hidden lg:flex items-center gap-4" data-id="element-179">
              {/* Language Toggle */}
              <div className={`flex items-center gap-1 rounded-full p-1 border transition-colors ${isScrolled ? 'bg-white/10 border-white/20' : 'bg-white/10 border-white/20'}`} data-id="element-180">
                <Globe size={14} className={`ml-2 ${isScrolled ? 'text-gray-300' : 'text-gray-300'}`} data-id="element-181" />
                <button onClick={() => setLanguage('en')} className={`px-2.5 py-1 text-xs font-bold rounded-full transition-colors ${language === 'en' ? 'bg-brand-gold text-brand-dark' : 'text-white hover:text-brand-gold'}`} data-id="element-182">
                  EN
                </button>
                <button onClick={() => setLanguage('es')} className={`px-2.5 py-1 text-xs font-bold rounded-full transition-colors ${language === 'es' ? 'bg-brand-gold text-brand-dark' : 'text-white hover:text-brand-gold'}`} data-id="element-183">
                  ES
                </button>
              </div>

              {/* CTA Button */}
              <a href="tel:3054170530" className="flex items-center gap-2 bg-brand-red hover:bg-brand-darkred text-white px-6 py-3 rounded-sm font-bold transition-all hover:shadow-lg hover:shadow-brand-red/20 text-sm" data-id="element-184">
                <PhoneCall size={18} className="animate-pulse" data-id="element-185" />
                <span data-id="element-186">{t.nav.freeConsultation}</span>
              </a>
            </div>

            {/* Mobile Actions */}
            <div className="flex items-center gap-3 lg:hidden" data-id="element-187">
              {/* Mobile Language Toggle */}
              <div className={`flex items-center gap-1 rounded-full p-1 border transition-colors ${isScrolled ? 'bg-white/10 border-white/20' : 'bg-white/10 border-white/20'}`} data-id="element-188">
                <button onClick={() => setLanguage('en')} className={`flex items-center gap-1 px-2 py-1 text-[10px] font-bold rounded-full transition-colors ${language === 'en' ? 'bg-brand-gold text-brand-dark' : 'text-white'}`} data-id="element-189">
                  <img src="https://flagcdn.com/w20/us.png" alt="EN" className="w-4 h-3 object-cover rounded-sm" data-id="element-190" />
                  EN
                </button>
                <button onClick={() => setLanguage('es')} className={`flex items-center gap-1 px-2 py-1 text-[10px] font-bold rounded-full transition-colors ${language === 'es' ? 'bg-brand-gold text-brand-dark' : 'text-white'}`} data-id="element-191">
                  <img src="https://flagcdn.com/w20/es.png" alt="ES" className="w-4 h-3 object-cover rounded-sm" data-id="element-192" />
                  ES
                </button>
              </div>

              {/* Mobile Menu Button */}
              <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className={`p-2 rounded-sm transition-colors ${isScrolled ? 'text-white hover:bg-white/10' : 'text-white hover:bg-white/10'}`} data-id="element-193">
                {isMobileMenuOpen ? <X size={28} data-id="element-194" /> : <Menu size={28} data-id="element-195" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Nav */}
        <div className={`lg:hidden overflow-hidden transition-all duration-300 ${isMobileMenuOpen ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-196">
          <div className="bg-white border-t border-gray-100 shadow-xl" data-id="element-197">
            <div className="px-4 pt-4 pb-6 space-y-1" data-id="element-198">
              {navLinks.map(link => <a key={link.name} href={link.href} onClick={() => setIsMobileMenuOpen(false)} className="block px-4 py-3 text-base font-bold text-gray-800 hover:text-brand-red hover:bg-brand-red/5 uppercase tracking-wide rounded-sm transition-colors" data-id="element-199">
                  {link.name}
                </a>)}
              <div className="pt-4 space-y-3 border-t border-gray-100 mt-2" data-id="element-200">
                <a href="tel:3054170530" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center justify-center gap-2 bg-brand-red text-white px-4 py-3.5 rounded-sm font-bold" data-id="element-201">
                  <PhoneCall size={18} data-id="element-202" />
                  <span data-id="element-203">(305) 417-0530</span>
                </a>
                <a href="tel:3057857829" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center justify-center gap-2 bg-brand-dark text-white px-4 py-3.5 rounded-sm font-bold" data-id="element-204">
                  <Phone size={18} data-id="element-205" />
                  <span data-id="element-206">(305) 785-7829</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </nav>
    </>;
}